import {
  useCompanyInitialMessage,
  useUpdateCompanyMessage,
  useCompaniesList,
} from '@/hooks/use-company/use-company';
import { Flex, Form, Input, Spin, Typography } from 'antd';
import { useCallback, useEffect, useState } from 'react';
import styles from './initial-message.module.scss';
import { Button } from '../button/button';
import { LoadingOutlined } from '@ant-design/icons';
import Callout from '@/components/callout/callout';
import Card from '@/components/card/card';
import { CompanySelectBh } from '../company-select-bh/company-select-bh';
import { useSelectedCompany } from '@/hooks/use-selected-company/use-selected-company';
import { useCompanyStore } from '@/store/app.store';
import { Trans, useTranslation } from 'react-i18next';

export function InitialMessage() {
  const { t } = useTranslation(['settings', 'common']);
  const [isEditing, setIsEditing] = useState(false);
  const { selectedCompany, setSelectedCompany } = useSelectedCompany({
    storageKey: 'selectedCompany_company',
  });
  const { companyName, setCompanyName } = useCompanyStore();
  const { companies } = useCompaniesList();

  useEffect(() => {
    if (companyName && companies) {
      const company = companies.find((c) => c.name === companyName);
      if (company) {
        setSelectedCompany((prev) =>
          prev?.id === company.id
            ? prev
            : { id: company.id, name: company.name, time_zone: company.time_zone }
        );
      }
    }
  }, [companyName, companies]);

  const handleCompanyChange = useCallback(
    (company: typeof selectedCompany) => {
      setSelectedCompany(company);
      setCompanyName(company?.name);
    },
    [setSelectedCompany, setCompanyName]
  );
  const { companyMessage, isLoading } = useCompanyInitialMessage(selectedCompany?.name);
  const [form] = Form.useForm();
  const { updateCompanyMess, isPendingUpdate } = useUpdateCompanyMessage(selectedCompany?.name);

  useEffect(() => {
    if (isEditing && companyMessage) {
      form.setFieldsValue({
        welcome_message: companyMessage.welcome_message || '',
      });
    }
  }, [companyMessage, form, isEditing]);

  const handleEdit = () => setIsEditing(true);

  const handleCancel = () => {
    form.resetFields();
    setIsEditing(false);
  };

  const handleSave = (values: { welcome_message?: string }) => {
    const company: { welcome_message?: string } = {};

    // Form seeds '' for an unset greeting; compare against '' or untouched reads as edited.
    if (
      values.welcome_message !== undefined &&
      values.welcome_message !== (companyMessage?.welcome_message ?? '')
    ) {
      company.welcome_message = values.welcome_message;
    }

    if (Object.keys(company).length === 0) {
      setIsEditing(false);
      return;
    }

    updateCompanyMess(
      {
        company_name: selectedCompany!.name,
        company,
      },
      {
        onSuccess: () => {
          setIsEditing(false);
        },
      }
    );
  };

  return (
    <Flex vertical gap={16}>
      <CompanySelectBh value={selectedCompany} onChange={handleCompanyChange} />
      {isLoading && (
        <div className={styles.loading}>
          <Spin indicator={<LoadingOutlined spin />} />
        </div>
      )}
      {companyMessage && (
        <Card hint={t('settings:initialMessage.hint')}>
          <Callout className={styles.hintAlert}>
            <Trans
              ns="settings"
              i18nKey="initialMessage.variables"
              components={{ var: <Typography.Text code /> }}
            />
            <p>
              <Trans
                ns="settings"
                i18nKey="initialMessage.example"
                components={{ b: <strong />, var: <Typography.Text code /> }}
              />
            </p>
          </Callout>

          <Form form={form} onFinish={handleSave}>
            <Flex vertical gap={16}>
              <div>
                <p className={styles.rowLabel}>{t('settings:initialMessage.welcomeLabel')}</p>
                {isEditing ? (
                  <Form.Item name="welcome_message" style={{ marginBottom: 0 }}>
                    <Input.TextArea
                      className={styles.messageTextarea}
                      rows={3}
                      autoSize={{ minRows: 3, maxRows: 10 }}
                    />
                  </Form.Item>
                ) : (
                  <>
                    {companyMessage.welcome_message ? (
                      <span>{companyMessage.welcome_message}</span>
                    ) : (
                      <span className={styles.notCreatedMess}>
                        {t('settings:initialMessage.notCreated')}
                      </span>
                    )}
                  </>
                )}
              </div>

              {isEditing ? (
                <Flex gap={8} className={styles.editSaveButtons}>
                  <Button buttonVariant="secondary" onClick={handleCancel}>
                    {t('common:actions.cancel')}
                  </Button>
                  <Button
                    buttonVariant="primary"
                    onClick={() => form.submit()}
                    loading={isPendingUpdate}
                  >
                    {t('common:actions.save')}
                  </Button>
                </Flex>
              ) : (
                <Button buttonVariant="primary" onClick={handleEdit}>
                  <span className={styles.editButtonText}>{t('common:actions.edit')}</span>
                </Button>
              )}
            </Flex>
          </Form>
        </Card>
      )}
    </Flex>
  );
}
